import { ClockUser } from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Pagination from "../../../components/Pagination";
import { useAuth } from "../../../hooks";
import { useRecentActivity } from "../../../hooks/get";
import { ACTION_TYPE_META } from "../../../shared/constants";
import Activities from "../components/Activities";
import Filter, { UserActivityFilterValues } from "../components/Filter";
import { IUserActivity, IUserDropdown } from "../interface";

dayjs.extend(relativeTime);

export default function UserActivityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<UserActivityFilterValues>({
    action_type: "",
    action_group: "",
    user_id: "",
    from_date: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    to_date: dayjs().format("YYYY-MM-DD"),
  });
  const [perPage, setPerPage] = useState(20);

  const ALL_ACTION_TYPES = useMemo(
    () => [
      { value: "", label: t("common.allActivities") },
      ...Object.keys(ACTION_TYPE_META).map((key) => ({
        value: key,
        label: t(`userActivity.actionType.${key}`, ACTION_TYPE_META[key]?.label || key),
      })),
    ],
    [t]
  );

  // Helper to group action types (copied from Filter)
  function getGroupedActionTypes(
    actionTypes: { value: string; label: string }[],
    t: TFunction
  ) {
    const filtered = actionTypes.filter((a) => a.value !== "");
    const groups = [
      {
        id: "authentication",
        label: t(
          "userActivity.actionTypeGroup.authentication",
          "Authentication"
        ),
        options: filtered.filter((a) =>
          ["LOGIN", "LOGOUT", "SIGN_UP", "SESSION_CONTINUED"].includes(a.value)
        ),
      },
      {
        id: "profile",
        label: t(
          "userActivity.actionTypeGroup.profile",
          "Profile & User Management"
        ),
        options: filtered.filter((a) =>
          [
            "PROFILE_UPDATE",
            "USER_ROLE_CHANGE",
            "USER_DELETE",
            "USER_DEACTIVATED",
            "USER_ACTIVATED",
          ].includes(a.value)
        ),
      },
      {
        id: "invite",
        label: t("userActivity.actionTypeGroup.invite", "Invitations"),
        options: filtered.filter((a) =>
          [
            "INVITE_USER_ACCEPT",
            "INVITE_USER_EMAIL_SENT",
            "INVITE_USER_DELETE",
            "INVITE_USER_UPDATE",
            "RESEND_INVITE",
          ].includes(a.value)
        ),
      },
      {
        id: "password",
        label: t("userActivity.actionTypeGroup.password", "Password Actions"),
        options: filtered.filter((a) =>
          [
            "PASSWORD_RESET_REQUEST",
            "PASSWORD_RESET_COMPLETE",
            "PASSWORD_CHANGE",
          ].includes(a.value)
        ),
      },
      {
        id: "roles",
        label: t("userActivity.actionTypeGroup.roles", "Roles & Permissions"),
        options: filtered.filter((a) =>
          ["ROLE_CREATE", "ROLE_UPDATE", "ROLE_DELETE"].includes(a.value)
        ),
      },
      {
        id: "preferences",
        label: t("userActivity.actionTypeGroup.preferences", "Preferences"),
        options: filtered.filter((a) =>
          [
            "DASHBOARD_PREFERENCE_UPDATE",
            "APP_LIST_PREFERENCE_UPDATE",
            "APP_DETAIL_PREFERENCE_UPDATE",
            "APP_COUNTRY_REPORT_PREFERENCE_UPDATE",
          ].includes(a.value)
        ),
      },
      {
        id: "bidding",
        label: t("userActivity.actionTypeGroup.bidding", "Biding"),
        options: filtered.filter((a) => ["BID_CHANGE"].includes(a.value)),
      },
      {
        id: "integration",
        label: t("userActivity.actionTypeGroup.integration", "Integrations"),
        options: filtered.filter((a) =>
          [
            "INTEGRATION_START",
            "INTEGRATION_COMPLETE",
            "ADDON_ACCOUNT_INTEGRATION_COMPLETE",
            "ADDON_ACCOUNT_INTEGRATION_CANCEL",
            "ONGOING_ACCOUNT_DELETE",
            "ADDON_ACCOUNT_DELETE",
            "ADDON_ACCOUNT_INTEGRATION_START",
            "ENTIRE_TRACKING_DELETE",
          ].includes(a.value)
        ),
      },
      {
        id: "billing",
        label: t("userActivity.actionTypeGroup.billing", "Billing & Subscriptions"),
        options: filtered.filter((a) =>
          [
            "TRIAL_STARTED",
            "TRIAL_ENDED",
            "SUBSCRIPTION_CREATED",
            "SUBSCRIPTION_RENEWED",
            "SUBSCRIPTION_UPGRADED",
            "SUBSCRIPTION_DOWNGRADED",
            "SUBSCRIPTION_CANCELLED",
            "SUBSCRIPTION_REACTIVATED",
            "PAYMENT_SUCCEEDED",
            "PAYMENT_FAILED",
            "REFUND_REQUESTED",
            "REFUND_PROCESSED",
          ].includes(a.value)
        ),
      },
    ];
    // const groupedValues = groups.flatMap((g) => g.options.map((o) => o.value));
    // const misc = filtered.filter((a) => !groupedValues.includes(a.value));
    // if (misc.length > 0) {
    //   groups.push({
    //     label: t("userActivity.actionTypeGroup.misc", "Other"),
    //     options: misc,
    //   });
    // }
    return groups.filter((g) => g.options.length > 0);
  }

  // Build params object without undefined values
  const params: Record<string, string | number> = { page, per_page: perPage };

  // --- Group logic for API call ---
  if (filters.action_group && !filters.action_type) {
    // Find all action types in the selected group
    const groupedActionTypes = getGroupedActionTypes(ALL_ACTION_TYPES, t);
    const group = groupedActionTypes.find((g) => g.id === filters.action_group);
    if (group) {
      params.action_type = group.options.map((o) => o.value).join(",");
    }
  } else if (filters.action_type) {
    params.action_type = filters.action_type;
  }
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.from_date) params.from_date = filters.from_date;
  if (filters.to_date) params.to_date = filters.to_date;

  const {
    data: activityData,
    isLoading,
    isPlaceholderData,
    refetch,
    isRefetching,
  } = useRecentActivity(params);

  // Build userOptions from users_dropdown in API response
  const userOptions: { value: string; label: string }[] = useMemo(() => {
    if (!activityData?.users_dropdown)
      return [{ value: "", label: t("common.allUsers") }];
    return [
      { value: "", label: t("common.allUsers") },
      ...activityData.users_dropdown.map((member: IUserDropdown) => ({
        value: member.id.toString(),
        label: `${member.name || member.email} ${member.email === user?.email ? "(Me)" : ""
          }`,
      })),
    ];
  }, [activityData?.users_dropdown, user?.email, t]);

  const activities: IUserActivity[] = useMemo(() => {
    const results = activityData?.results || [];
    return results.map((activity: IUserActivity) => ({
      ...activity,
      time: dayjs(activity.created_at).fromNow(),
    }));
  }, [activityData]);

  const handleFilterChange = (newValues: UserActivityFilterValues) => {
    setPage(1);
    setFilters(newValues);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center pl-2">
        <div className="flex items-center gap-2 mb-4">
          <ClockUser size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
          <h1 className="text-sm font-bold text-gray-700 dark:text-gray-300">
            {t("userActivity.title")}
          </h1>
        </div>
        <Filter
          values={filters}
          isLoading={isLoading}
          onChange={handleFilterChange}
          actionTypes={ALL_ACTION_TYPES}
          userOptions={userOptions}
          refetch={refetch}
          isRefetching={isRefetching}
          allowTodaySelection={true}
        />
      </div>
      <div className="flex-1 overflow-y-auto mb-4 pl-4">
        <Activities
          key={(isLoading || isPlaceholderData || isRefetching).toString()}
          activityData={activities}
          isLoading={isLoading || isPlaceholderData || isRefetching}
        />
      </div>
      {activityData && activityData.total > 0 && (
        <div className="border-t border-gray-200 dark:border-neutral-700 pt-4 pb-2 px-2">
          <Pagination
            currentPage={page}
            totalPages={activityData.last_page}
            totalItems={activityData.total}
            itemsPerPage={perPage}
            onPageChange={setPage}
            onItemsPerPageChange={(per_page) => {
              setPage(1);
              setPerPage(per_page);
            }}
            loading={isLoading}
          />
        </div>
      )}
    </div>
  );
}
